import { requireAuth, useAuth } from '@/features/auth'
import { UserRole } from '@/features/auth/schemas/user.schema'
import { createFileRoute, Link, Outlet, useLocation } from '@tanstack/react-router'
import {
  Sparkles,
  Inbox,
  Users,
  LogOut,
  PanelLeft,
  Newspaper,
  Bell,
  LayoutDashboard,
} from 'lucide-react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Separator } from '@/components/ui/separator'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar'

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: requireAuth,
  component: AuthenticatedLayout,
})

// Navigation items
const mainNavItems = [
  { title: 'Dashboard', icon: LayoutDashboard, url: '/dashboard' },
  { title: 'Caschi', icon: Sparkles, url: '/helmets' },
  { title: 'Venditori', icon: Inbox, url: '/sellers' },
  { title: 'Utenti', icon: Users, url: '/app-users' },
  { title: 'Acquisti', icon: Users, url: '/app-user-helmets' },
  { title: 'News', icon: Newspaper, url: '/news' },
  { title: 'Notifiche', icon: Bell, url: '/notifications/new' },
]

function AppSidebar() {
  const { currentUser, cognitoUser, signOut } = useAuth()
  const location = useLocation()
  const userEmail = currentUser?.email || cognitoUser?.signInDetails?.loginId || 'm@example.com'

  return (
    <Sidebar className="bg-sidebar">
      <SidebarHeader className="gap-3 px-2 pb-0 pt-2">
        {/* Company Selector */}
        <SidebarMenu>
          <SidebarMenuItem>
              <img src="/images/logo.png" alt="Logo" className="w-13.75 ml-5 mt-2" />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {/* Main Navigation */}
        <SidebarGroup className="px-2">
          <SidebarMenu>
            {mainNavItems.map((item) => {
              const isActive =
                item.url !== '#' &&
                (location.pathname === item.url ||
                  location.pathname.startsWith(item.url + '/'))
              return (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={isActive}>
                    <Link to={item.url}>
                      <item.icon className="size-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="gap-2 px-2 pb-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={signOut}>
              <LogOut className="size-4" />
              <span>Logout</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>

        {/* User Menu */}
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" className="gap-2">
              <Avatar className="size-8 rounded-lg">
                <AvatarImage src="" alt={userEmail} />
                <AvatarFallback className="rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                  {userEmail.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-1 flex-col text-left text-sm leading-tight">
                <span className="truncate text-xs text-muted-foreground">
                  {userEmail}
                </span>
                {currentUser?.role === UserRole.Admin && (
                  <span className="truncate text-xs text-muted-foreground">
                    Admin
                  </span>
                )}
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}

const pageTitles: Record<string, string> = Object.fromEntries(
  mainNavItems.map((item) => [item.url, item.title]),
)

function AuthenticatedLayout() {
  const location = useLocation()
  
  // Find matching page title by checking if pathname starts with any nav item URL
  const pageTitle = Object.entries(pageTitles).find(([url]) => 
    location.pathname === url || location.pathname.startsWith(url + '/')
  )?.[1] ?? ''

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="bg-sidebar">
        {/* Main Content Wrapper */}
        <div className="flex flex-col h-full p-2">
          <div className="flex-1 rounded-lg border-[0.5px] bg-[#fbfaf8] overflow-hidden flex flex-col">
            {/* Header */}
            <header className="flex h-14 items-center gap-2 border-b-[0.5px] px-4 shrink-0 bg-background">
              <SidebarTrigger className="size-7 cursor-pointer">
                <PanelLeft className="size-4" />
              </SidebarTrigger>
              <div className="pr-2">
                <Separator orientation="vertical" className="!h-6" />
              </div>
              <span className="flex-1 font-semibold text-foreground">{pageTitle || 'Dashboard'}</span>
            </header>

            {/* Content */}
            <main className="flex-1 overflow-y-auto overflow-x-hidden">
              <Outlet />
            </main>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
